/**
 * GroupFormSection Component
 * Handles group name, type, and VPE selection
 */

import React from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Users, ChevronDown } from 'lucide-react';
import MultiSelect from '@/components/common/MultiSelect';

interface GroupType {
  id: number;
  group_type: string;
}

interface VPEOption {
  value: number;
  label: string;
}

interface GroupDetails {
  es_name?: string;
  [key: string]: any;
}

interface GroupFormSectionProps {
  // State
  groupName: string;
  selectedGroupType: string;
  selectedVPEs: number[];
  createType: 'filters' | 'emrs' | '';
  groupTypes: GroupType[];
  vpeOptions: VPEOption[];
  isDuplicateMode: boolean;
  isEditMode: boolean;
  groupDetails?: GroupDetails;
  isLoadingSpecialists?: boolean;
  
  // Actions
  onGroupNameChange: (value: string) => void;
  onGroupTypeChange: (value: string) => void;
  onVPEChange: (values: number[]) => void;
  onCreateTypeChange: (value: 'filters' | 'emrs') => void;
  onVpeManuallyInteracted: () => void;
}

export const GroupFormSection: React.FC<GroupFormSectionProps> = ({
  groupName,
  selectedGroupType,
  selectedVPEs,
  createType,
  groupTypes,
  vpeOptions,
  isDuplicateMode,
  isEditMode,
  groupDetails,
  onGroupNameChange,
  onGroupTypeChange,
  onVPEChange,
  onCreateTypeChange,
  onVpeManuallyInteracted,
  isLoadingSpecialists = false
}) => {
  const handleCreateTypeChange = (value: string) => {
    if (value === 'filters' || value === 'emrs') {
      onCreateTypeChange(value);
    }
  };

  const handleVPEChange = (values: number[]) => {
    console.log('GroupFormSection onChange - received values:', values);
    console.log('GroupFormSection onChange - current selectedVPEs:', selectedVPEs);
    
    // Remove duplicates as a safety measure
    const uniqueValues = [...new Set(values)];
    if (uniqueValues.length !== values.length) {
      console.warn('GroupFormSection: Duplicates detected and removed:', values, '->', uniqueValues);
    }
    
    onVpeManuallyInteracted();
    onVPEChange(uniqueValues);
  };

  if (isEditMode) {
    return (
      <div className="space-y-6">
        {/* Group Name Section */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <Users className="h-6 w-6 text-purple-600" />
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Group Name</label>
              <Input
                value={groupName}
                onChange={(e) => onGroupNameChange(e.target.value)}
                className="text-xl font-bold border-gray-300 focus:border-purple-500"
                placeholder="Enter group name"
              />
            </div>
          </div>
        </div>

        {/* Enrollment Specialist */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <label className="block text-lg font-medium text-gray-700 mb-4">
            Enrollment Specialist
          </label>
          {groupDetails?.es_name ? (
            <div className="space-y-4">
              {/* Display existing VPE names from group details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Assigned enrollment specialist:</div>
                <div className="flex flex-wrap gap-2">
                  {groupDetails.es_name.split(', ').map((name: string, index: number) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                      {name.trim()}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Filterable dropdown for VPE selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Update Enrollment Specialist Selection:
                </label>
                <MultiSelect
                  options={vpeOptions}
                  value={selectedVPEs}
                  onChange={handleVPEChange}
                  placeholder={isLoadingSpecialists ? "Loading specialists..." : "Select available specialists"}
                  className="w-full"
                  disabled={isLoadingSpecialists}
                />
              </div>
            </div>
          ) : (
            <MultiSelect
              options={vpeOptions}
              value={selectedVPEs}
              onChange={handleVPEChange}
              placeholder={isLoadingSpecialists ? "Loading specialists..." : "Select Enrollment Specialist"}
              className="w-full"
              disabled={isLoadingSpecialists}
            />
          )}
        </div>

        {/* Type */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <label className="block text-lg font-medium text-gray-700 mb-4">
            Type
          </label>
          <Select value={selectedGroupType} onValueChange={onGroupTypeChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Type" />
            </SelectTrigger>
            <SelectContent>
              {groupTypes.map((type) => (
                <SelectItem key={type.id} value={type.id.toString()}>
                  {type.group_type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Group Name */}
      <div className="flex items-center space-x-2 mb-4">
        <Users className="h-5 w-5 text-purple-600" />
        <span className="text-lg font-semibold">Group Name*</span>
      </div>
      <Input
        placeholder="Enter group name..."
        value={groupName}
        onChange={(e) => onGroupNameChange(e.target.value)}
        className="w-full"
      />

      {/* Create Group By Dropdown - Hidden in duplicate mode */}
      {!isDuplicateMode && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Create Group By*
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="bg-purple-600 hover:bg-purple-700 text-white border-purple-600 px-4 py-2 rounded-md font-medium"
              >
                {createType ? (createType === 'filters' ? 'Filters' : 'EMRs') : 'Create Group by'}
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white border shadow-lg rounded-md p-1 min-w-[120px]">
              <DropdownMenuItem 
                onClick={() => handleCreateTypeChange('filters')}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer rounded"
              >
                Filters
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleCreateTypeChange('emrs')}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer rounded"
              >
                EMRs
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Show form when type is selected */}
      {createType && (
        <>
          {/* VPE Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Virtual Enrollment Specialist (VPE)
              {createType === 'emrs' && <span className="text-gray-500 font-normal"> (Optional)</span>}
            </label>
            <MultiSelect
              options={vpeOptions}
              value={selectedVPEs}
              onChange={handleVPEChange}
              placeholder={isLoadingSpecialists ? "Loading specialists..." : "Select Virtual Enrollment Specialist"}
              className="w-full"
              disabled={isLoadingSpecialists}
            />
          </div>

          {/* Group Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Group Type*
            </label>
            <Select value={selectedGroupType} onValueChange={onGroupTypeChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Type" />
              </SelectTrigger>
              <SelectContent>
                {groupTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id.toString()}>
                    {type.group_type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}
    </div>
  );
};